import { useEffect } from 'react';

/**
 * Hook to scroll to an element when the URL hash matches its ID.
 * Uses MutationObserver to handle cases where the element might not be in the DOM yet.
 *
 * @param {string} hash - The hash string (e.g., from useLocation)
 */
export default function useHashScroll(hash) {
  useEffect(() => {
    if (!hash) return;

    const id = hash.replace('#', '');

    // Try immediately first
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Fall back to watching the DOM for the element to appear
    const observer = new MutationObserver(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        observer.disconnect(); // Stop watching once found
      }
    });

    observer.observe(document.body, {
      childList: true, // Watch for added/removed nodes
      subtree: true, // Watch all descendants
    });

    // Cleanup if the component unmounts before element appears
    return () => observer.disconnect();
  }, [hash]);
}
